<template>
    <v-row class="pt-2">
        <v-col cols="12">
            <v-hover v-slot="{ hover }">
                <v-card :elevation="hover ? 6 : 2" :disabled="loading">
                    <v-card-title><v-spacer />
                        <v-col cols="3">
                            <v-autocomplete placeholder="เลือกมิเตอร์" :disabled="loading" class="mt-6" dense solo
                                v-model="selectedMeter" multiple small-chips deletable-chips
                                :items="assets" item-text="assetName" item-value="assetId"></v-autocomplete></v-col>
                        <v-col cols="2"> <v-autocomplete :disabled="loading" class="mt-6" dense solo v-model="selectedPeriod"
                                :items="periods" item-text="period" item-value="value"></v-autocomplete></v-col>
                        <v-col cols="auto">
                            <DateRangePicker class="mt-6" v-model="range" :timePicker="false"/>
                        </v-col>
                        <v-col cols="auto"> <v-btn color="primary" :disabled="loading"
                                @click="getEnergyTable()">View</v-btn></v-col><v-col cols="auto"> <v-btn
                                :disabled="loading" color="primary" outlined
                                @click="exportToXLS(`Export${$dayjs().format('DDMMMYYYYHHmmss')}`, { headers: headers, items: items })">Export</v-btn></v-col>
                    </v-card-title>
                </v-card>
            </v-hover>
        </v-col>
        <v-col cols="12">
            <STACK_BAR_CHART v-bind="series" :update="update"/>
        </v-col>
        <v-col cols="12" v-if="periodSummary">
            <v-row>
                <v-col cols="12">
                    <div class="text-subtitle-1 font-weight-medium grey--text text--darken-1">
                        <v-icon small>mdi-calendar-range</v-icon>
                        Summary — {{ periodLabel }}
                    </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" v-for="card in summaryCards" :key="card.key">
                    <v-hover v-slot="{ hover }">
                        <v-card :elevation="hover ? 6 : 2" class="pa-4">
                            <div class="text-subtitle-2 grey--text text--darken-1">{{ card.label }}</div>
                            <div class="text-h5 font-weight-bold text-right mt-2">
                                {{ card.value }}
                                <span class="text-subtitle-1 font-weight-regular">{{ card.unit }}</span>
                            </div>
                        </v-card>
                    </v-hover>
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="12">
            <v-hover v-slot="{ hover }">
                <v-card :elevation="hover ? 6 : 2">
                    <v-card-text>
                        <v-data-table :headers="headers" :items="items" :items-per-page="-1">
                            <template v-slot:item.Peak="{ item }">
                                {{ item.Peak.toFixed(2) }}
                            </template>
                            <template v-slot:item.OffPeak="{ item }">
                                {{ item.OffPeak.toFixed(2) }}
                            </template>
                            <template v-slot:item.demand="{ item }">
                                {{ item.demand.toFixed(2) }}
                            </template>
                            <template v-slot:item.Unit="{ item }">
                                {{ item.Unit.toFixed(2) }}
                            </template>
                            <template v-slot:item.date="{ item }">
                                {{ $dayjs(item.date, "YY-MM-DD").format('DD/MM/YYYY') }}
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-hover>
        </v-col>
    </v-row>
</template>
<script>
import { xlsxMixins } from '@/mixins/xlsx'
import DateRangePicker from '@/components/common/DateRangePicker.vue'
import STACK_BAR_CHART from '@/components/charts/stack_bar_chart.vue';
export default {
    mixins: [xlsxMixins],
    components: {
        DateRangePicker,
        STACK_BAR_CHART
    },
    props: {
        update: {
            type: Boolean
        }
    },
    data() {
        return {
            range: {
                startDate: this.$dayjs().startOf('month').toDate(),
                endDate: this.$dayjs().endOf('month').toDate(),
            },
            selectedPeriod: 'daily',
            periods: [
                { period: 'Daily', value: 'daily' },
                { period: 'Weekly', value: 'weekly' },
                { period: 'Monthly', value: 'monthly' },
                { period: 'Yearly', value: 'yearly' },
            ],
            selectedMeter: [],
            loading: false,
            headers: [],
            items: [],
            series: {
                axis: [],
                peak: [],
                offPeak: [],
                demand: []
            },
        }
    },
    computed: {
        assets() {
            return this.$store.state.Asset.assetEnergy.filter((el) => el.assetType === 'PowerMeter')
        },
        building: {
            get: function () {
                return this.$store.state.User.building
            },
            set: function (newValue) {
                this.$store.dispatch('User/SET_BUILDING', newValue)
            },
        },
        periodLabel() {
            const periodName = this.periods.find((p) => p.value === this.selectedPeriod)?.period || ''
            const start = this.$dayjs(this.range.startDate).format('DD/MM/YYYY')
            const end = this.$dayjs(this.range.endDate).format('DD/MM/YYYY')
            return `${periodName} (${start} - ${end})`
        },
        periodSummary() {
            if (!this.items.length) return null
            return this.items.reduce((acc, item) => {
                acc.peak += Number(item.Peak) || 0
                acc.offPeak += Number(item.OffPeak) || 0
                acc.total += Number(item.Unit) || 0
                acc.demand = Math.max(acc.demand, Number(item.demand) || 0)
                return acc
            }, { peak: 0, offPeak: 0, total: 0, demand: 0 })
        },
        summaryCards() {
            if (!this.periodSummary) return []
            const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
            return [
                { key: 'peak', label: 'Peak', value: fmt(this.periodSummary.peak), unit: 'kWh' },
                { key: 'offPeak', label: 'Off-Peak', value: fmt(this.periodSummary.offPeak), unit: 'kWh' },
                { key: 'total', label: 'Total', value: fmt(this.periodSummary.total), unit: 'kWh' },
                { key: 'demand', label: 'Peak Demand', value: fmt(this.periodSummary.demand), unit: 'kW' },
            ]
        },
    },
    watch: {
        building() {
            this.selectedMeter = []
            this.headers = []
            this.items = []
        }
    },
    methods: {
        getMonthlyChunks(startDate, endDate) {
            const chunks = []
            let current = this.$dayjs(startDate).startOf('day')
            const end = this.$dayjs(endDate).endOf('day')
            while (current.isBefore(end) || current.isSame(end, 'day')) {
                const monthEnd = current.endOf('month')
                const chunkEnd = monthEnd.isAfter(end) ? end : monthEnd
                chunks.push({
                    start: current.toDate().getTime(),
                    end: chunkEnd.toDate().getTime(),
                })
                current = chunkEnd.add(1, 'day').startOf('day')
            }
            return chunks
        },
        fetchEnergyTable(start, end) {
            const group = this.selectedMeter.length > 1
            return this.$store.dispatch('Energy/GET_ENERGY_TABLE', {
                BuildingId: this.building.BuildingId,
                type: this.selectedPeriod,
                assetId: this.selectedMeter,
                group,
                start,
                end,
            })
        },
        sortEnergyData(data) {
            switch (this.selectedPeriod) {
                case 'daily':
                    return data.sort((a, b) => this.$dayjs(a.date, 'YY-MM-DD').valueOf() - this.$dayjs(b.date, 'YY-MM-DD').valueOf())
                case 'weekly':
                    return data.sort((a, b) => (a.year - b.year) || (a.week - b.week))
                case 'monthly':
                    return data.sort((a, b) => (a.year - b.year) || (a.month - b.month))
                case 'yearly':
                    return data.sort((a, b) => a.year - b.year)
                default:
                    return data
            }
        },
        mergeEnergyData(chunks) {
            return this.sortEnergyData(chunks.flat())
        },
        applyEnergyData(data) {
            this.items = []
            this.series.axis = []
            this.series.peak = []
            this.series.offPeak = []
            this.series.demand = []
            switch (this.selectedPeriod) {
                case 'daily':
                    this.headers = [
                        { text: 'Date', value: 'date' },
                        { text: 'Peak', value: 'Peak' },
                        { text: 'OffPeak', value: 'OffPeak' },
                        { text: 'Total', value: 'Unit' },
                        { text: 'Demand', value: 'demand' },
                    ]
                    data.forEach((element) => {
                        this.series.axis.push(element.date)
                        this.series.peak.push(Math.ceil(element.Peak))
                        this.series.offPeak.push(Math.ceil(element.OffPeak))
                        this.series.demand.push(Math.ceil(element.demand))
                    })
                    break;
                case 'weekly':
                    this.headers = [
                        { text: 'Year', value: 'year' },
                        { text: 'Week', value: 'week' },
                        { text: 'Peak', value: 'Peak' },
                        { text: 'OffPeak', value: 'OffPeak' },
                        { text: 'Total', value: 'Unit' },
                        { text: 'Demand', value: 'demand' },
                    ]
                    data.forEach((element) => {
                        this.series.axis.push(element.week)
                        this.series.peak.push(Math.ceil(element.Peak))
                        this.series.offPeak.push(Math.ceil(element.OffPeak))
                        this.series.demand.push(Math.ceil(element.demand))
                    })
                    break;
                case 'monthly':
                    this.headers = [
                        { text: 'Year', value: 'year' },
                        { text: 'Month', value: 'month' },
                        { text: 'Peak', value: 'Peak' },
                        { text: 'OffPeak', value: 'OffPeak' },
                        { text: 'Total', value: 'Unit' },
                        { text: 'Demand', value: 'demand' },
                    ]
                    data.forEach((element) => {
                        this.series.axis.push(this.$dayjs().set('month', element.month - 1).format('MMM YY'))
                        this.series.peak.push(Math.ceil(element.Peak))
                        this.series.offPeak.push(Math.ceil(element.OffPeak))
                        this.series.demand.push(Math.ceil(element.demand))
                    })
                    break;
                case 'yearly':
                    this.headers = [
                        { text: 'Year', value: 'year' },
                        { text: 'Peak', value: 'Peak' },
                        { text: 'OffPeak', value: 'OffPeak' },
                        { text: 'Total', value: 'Unit' },
                        { text: 'Demand', value: 'demand' },
                    ]
                    data.forEach((element) => {
                        this.series.axis.push(element.year)
                        this.series.peak.push(Math.ceil(element.Peak))
                        this.series.offPeak.push(Math.ceil(element.OffPeak))
                        this.series.demand.push(Math.ceil(element.demand))
                    })
                    break;
            }
            this.items = data
        },
        getEnergyTable() {
            if (!this.selectedMeter.length) return
            this.loading = true
            const start = this.range.startDate.getTime()
            const end = this.range.endDate.getTime()
            const requests = this.selectedPeriod === 'daily' && !this.$dayjs(start).isSame(end, 'month')
                ? this.getMonthlyChunks(this.range.startDate, this.range.endDate).map((chunk) => this.fetchEnergyTable(chunk.start, chunk.end))
                : [this.fetchEnergyTable(start, end)]

            Promise.all(requests).then((results) => {
                const data = this.mergeEnergyData(results)
                this.applyEnergyData(data)
            }).catch(() => {
                // keep previous data on error
            }).finally(() => {
                this.loading = false
            })
        },
    },
}

</script>